import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Warning } from '@phosphor-icons/react';
import { labelClass, riskClass, fmtD } from './planningUtils';

export function PendingAllocationsDialog({ open, onOpenChange, onAssign }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/production-planning/pending');
      setRows(data.rows || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl bg-white max-h-[85vh] overflow-y-auto" data-testid="pending-allocations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warning size={22} className="text-red-600" weight="fill" />
            Job Orders Pending Production Allocation
            <Badge className="bg-red-600 text-white">{rows.length}</Badge>
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="p-6 text-center text-sm text-zinc-400">Loading…</div> : (
          rows.length === 0 ? <div className="p-6 text-center text-sm text-emerald-600">All job orders are allocated. Nothing pending.</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">JO No</th>
                    <th className="p-2 text-left">Customer</th>
                    <th className="p-2 text-left">JO Date</th>
                    <th className="p-2 text-left">Delivery</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-left">Procurement</th>
                    <th className="p-2 text-right">Req. Days</th>
                    <th className="p-2 text-left">Earliest Slot</th>
                    <th className="p-2 text-left">Proposed End</th>
                    <th className="p-2 text-right">Days Left</th>
                    <th className="p-2 text-left">Reason</th>
                    <th className="p-2 text-left">Risk</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`pending-row-${r.jo_number}`}>
                      <td className="p-2 font-mono font-bold text-blue-700">{r.jo_number}</td>
                      <td className="p-2">{r.customer_name}</td>
                      <td className="p-2">{fmtD(r.jo_date)}</td>
                      <td className="p-2">{fmtD(r.delivery_date)}</td>
                      <td className="p-2 text-right font-bold">{r.total_qty}</td>
                      <td className="p-2"><Badge className={`text-[10px] ${labelClass(r.procurement_status === 'Pending Procurement' ? 'Pending Procurement – Tentative' : 'Ready to Assign')}`}>{r.procurement_status}</Badge></td>
                      <td className="p-2 text-right">{r.required_days}</td>
                      <td className="p-2">{fmtD(r.earliest_available_date)}</td>
                      <td className="p-2">{fmtD(r.proposed_completion)}</td>
                      <td className={`p-2 text-right font-bold ${(r.days_to_delivery ?? 99) < 0 ? 'text-red-600' : ''}`}>{r.days_to_delivery ?? '—'}</td>
                      <td className="p-2 max-w-[220px]">{(r.reasons || []).join('; ')}</td>
                      <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${riskClass(r.risk_level)}`}>{r.risk_level}</span></td>
                      <td className="p-2 whitespace-nowrap">
                        {r.status === 'procurement_approved' ? (
                          <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 mr-1"
                            data-testid={`pending-assign-${r.jo_number}`}
                            onClick={() => { onOpenChange(false); onAssign && onAssign(r.jo_number); }}>
                            Assign
                          </Button>
                        ) : (
                          <Badge className="text-[10px] bg-orange-100 text-orange-700 mr-1">Approve Procurement First</Badge>
                        )}
                        <Button size="sm" variant="outline" className="h-6 text-[10px]"
                          onClick={() => navigate(`/job-orders/${r.jo_number}`)}>Open JO</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
